import { ColorModel } from '~/core/models';
import type { ColorState } from '~/redux/slices/color/color.types';

/**
 * Decide whether the Redux color list must be (re)seeded for a material.
 *
 * `color.byId` always holds the material's FULL color set; sorting only
 * reorders `color.usableIds` and filtering only narrows it, so the check below
 * stays `false` while the user sorts/filters — which is what stops the seeding
 * effect from clobbering their selection (SD-1280). It returns `true` only on
 * first load (empty `byId`) or when the material's color set changes.
 *
 * A material with no colors returns `false` only when `byId` is already empty;
 * if a previous material's colors are still in `byId` it returns `true` so the
 * stale list is cleared (loop-safe — the next pass sees an empty `byId`).
 */
export const shouldReseedColorList = (
  currentColorsById: ColorState['byId'],
  materialColors: ColorModel[]
): boolean => {
  const expectedIds = new Set(materialColors.map((color) => color.productId));

  // No colors for this material: only reseed (to clear) if a stale list remains.
  if (expectedIds.size === 0) {
    return Object.keys(currentColorsById).length > 0;
  }

  if (Object.keys(currentColorsById).length !== expectedIds.size) {
    return true;
  }

  return [...expectedIds].some((id) => currentColorsById[id] === undefined);
};
